import {
  IInvoice,
  Invoice,
  InvoiceLine,
  IStockReceived,
  SalesTax,
  StockReceived,
} from '$models'
import dayjs, { Dayjs } from 'dayjs'
import { formatNumberToDecimalPlaces } from 'src/components/common/helpers'
import { UiInvoice } from 'src/components/routes/stock/invoices/edit/helpers/uiInvoice'
import { StockReceivedRow } from '../../../_common/helpers/stockReceivedRow'
import { calculateFooterTotals } from 'src/components/routes/stock/_common/helpers/helper'
import { transformStringToNumberProperty } from '$utils'
import { API_DATE_FORMAT } from '$constants'

export const calculateSupplierStatementTotals = (
  stockReceivedItems: IStockReceived[],
  supplierStatementAmount?: number
) => {
  const totalAmountReconciled = stockReceivedItems.reduce((sum, order) => {
    if (order.reconciled && order.total) {
      return sum + order.total
    }
    return sum
  }, 0)

  const totalAmountOnStatement = formatNumberToDecimalPlaces(
    supplierStatementAmount
  )
  const difference =
    totalAmountOnStatement - formatNumberToDecimalPlaces(totalAmountReconciled)

  return {
    totalAmountOnStatement,
    totalAmountReconciled,
    difference,
  }
}

export const transformAdjustingInvoiceToInvoiceExtended = (
  adjustingInvoice: IInvoice,
  salesTax: SalesTax[],
  periodEnd: Dayjs,
  isEditView: boolean
) => {
  const transformedInvoice: UiInvoice = {
    ...adjustingInvoice,

    // We can't rely on the result.id because the default Adjusting Invoice is retrieved
    // with a Guid empty string, so we need to set it to undefined but if the user is viewing
    // an existing Adjusting Invoice we need to use its id
    id: isEditView ? adjustingInvoice.id : undefined,
    dueAt: isEditView ? adjustingInvoice.dueAt : periodEnd.format('YYYY-MM-DD'),
    issuedAt: isEditView
      ? adjustingInvoice.issuedAt
      : periodEnd.format('YYYY-MM-DD'),

    lines:
      adjustingInvoice.lines?.map((line, index) => {
        return {
          ...line,
          id: window.crypto.randomUUID(),
          index: index,
          selectedSalesTax:
            salesTax.find((tax) => tax.rate === line.saleTaxRate)?.sortOrder ??
            -1,
          customTaxRate: (line.saleTaxRate ?? 0) * 100,
          lineClass: () => '',
        }
      }) ?? [],
  }

  return transformedInvoice
}

/**
 * Formats an Adjusting Invoice form object to be ready to be saved
 * @param adjustingInvoice the invoice to be saved
 * @returns Invoice ready to be sent to the backend
 */
export const transformAdjustingInvoiceOnSave = (
  adjustingInvoice: UiInvoice
): Invoice => {
  const { subtotal, taxTotal, total } = calculateFooterTotals(
    adjustingInvoice.lines,
    adjustingInvoice?.unitCostIncludesTax ?? false,
    adjustingInvoice?.total
  )

  return new Invoice({
    ...adjustingInvoice,
    issuedAt: adjustingInvoice.issuedAt
      ? dayjs(adjustingInvoice.issuedAt).format(API_DATE_FORMAT)
      : undefined,
    notes: adjustingInvoice.notes ? adjustingInvoice.notes : undefined,
    referenceNumber: adjustingInvoice.referenceNumber
      ? adjustingInvoice.referenceNumber
      : undefined,
    total,
    subtotal,
    taxAmount: taxTotal,
    lines: adjustingInvoice.lines?.map((line) => {
      // Destructuring to remove
      const {
        index: _index,
        selectedSalesTax: _selectedSalesTax,
        customTaxRate: _customTaxRate,
        isInvoiceLine: _isInvoiceLine,
        isPurchaseOrderLine: _isPurchaseOrderLine,
        isNew: _isNew,
        ...restLine // Spread the remaining line properties
      } = line

      return new InvoiceLine({
        ...restLine,
        unitCost:
          typeof line.unitCost === 'string'
            ? line.unitCost === ''
              ? undefined
              : transformStringToNumberProperty(line.unitCost)
            : line.unitCost,
        totalCost:
          typeof line.totalCost === 'string'
            ? line.totalCost === ''
              ? undefined
              : transformStringToNumberProperty(line.totalCost)
            : line.totalCost,
      })
    }),
  })
}

export const addStockReceivedOrdered = (
  stockReceiveList: StockReceivedRow[],
  invoice: IInvoice
): StockReceivedRow[] => {
  const newStockReceived: IStockReceived = new StockReceived({
    ...invoice,
    invoicedAt: invoice.issuedAt,
  })

  const newStockReceiveRow: StockReceivedRow = {
    ...newStockReceived,
    isEditable: true,
    isDisabled: false,
    isWithinPeriod: true,
    isReconciledToAnotherStatement: false,
  }

  // Find the correct position based on 'invoicedAt'
  const insertIndex = stockReceiveList.findIndex((stockReceive) => {
    // Check if newStockReceived should be inserted before the current item
    if (newStockReceiveRow.invoicedAt && stockReceive.invoicedAt) {
      return dayjs(newStockReceiveRow.invoicedAt).isBefore(
        stockReceive.invoicedAt
      )
    }
    // Handle cases where invoicedAt might be undefined
    return false
  })

  // If the newStockReceived should be inserted at the end, push it
  if (insertIndex === -1) {
    stockReceiveList.push(newStockReceiveRow)
  } else {
    // Otherwise, insert it at the found position
    stockReceiveList.splice(insertIndex, 0, newStockReceiveRow)
  }

  return stockReceiveList
}
